import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { FiMoreVertical } from 'react-icons/fi'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTableActionsProps, ActionButton } from '../types'

export function DataTableActions<T>({ actions }: DataTableActionsProps<T>) {
  const { t } = useTranslation()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [confirmAction, setConfirmAction] = useState<ActionButton | null>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)

  const handleActionClick = (action: ActionButton) => {
    if (action.confirmMessage) {
      setConfirmAction(action)
      onOpen()
    } else {
      action.onClick()
    }
  }

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction.onClick()
    }
    setConfirmAction(null)
    onClose()
  }

  const handleCancel = () => {
    setConfirmAction(null)
    onClose()
  }

  if (!actions || actions.length === 0) {
    return null
  }

  // Always use dropdown menu for consistent UX
  return (
    <>
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<FiMoreVertical />}
          size="xs"
          variant="ghost"
          aria-label={t('common.actions')}
        />
        <MenuList>
          {actions.map((action, index) => (
            <MenuItem
              key={index}
              icon={action.icon as any}
              onClick={() => handleActionClick(action)}
              isDisabled={action.disabled}
            >
              {action.label || action.tooltip || action.ariaLabel || t('common.actions')}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCancel}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('common.confirm')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {confirmAction?.confirmMessage}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme={confirmAction?.colorScheme || 'red'}
                onClick={handleConfirm}
                ml={3}
              >
                {t('common.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
